import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchSpeakersPage } from '@/store/slices/speakers';
import { selectSpeakerOptionsState } from '@/store/slices/speakers/selectors';
import { getActiveChannel } from '@/store/slices/user/selectors';

const useSpeakerOptions = () => {
  const channel = useAppSelector(getActiveChannel);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const dispatch = useAppDispatch();

  useEffect(() => {
    const search = searchInput.trim();

    if (!search) {
      setDebouncedSearch('');
      return;
    }

    const timeout = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const optionsSelector = useMemo(() => selectSpeakerOptionsState(debouncedSearch), [debouncedSearch]);
  const { speakers, loading, hasMore, page } = useAppSelector(optionsSelector);

  useEffect(() => {
    if (!channel || !debouncedSearch) return;

    dispatch(fetchSpeakersPage({ channel, page: 1, search: debouncedSearch }));
  }, [channel, debouncedSearch, dispatch]);

  const onMenuEndReached = useCallback(() => {
    if (!channel || loading || !hasMore) return;

    dispatch(fetchSpeakersPage({ channel, page: page + 1, search: debouncedSearch || undefined }));
  }, [channel, debouncedSearch, dispatch, hasMore, loading, page]);

  return {
    speakers,
    loading,
    onSearchChange: setSearchInput,
    onMenuEndReached,
    disableClientFilter: !!debouncedSearch,
  };
};

export default useSpeakerOptions;
